<script setup>
import { ref, computed, inject } from 'vue';
import { useRoute } from 'vue-router';
import RandomImages from '/app/components/challenge/RandomImages.vue';
import { useChallengeCreationFlow } from '/app/composables/challenge-create-flow';
import { useFormContext } from '/app/composables/form';
import NIcon from '/app/components/NIcon.vue';
import NTags from '/app/components/form/Tags.vue';
import NFile from '/app/components/form/File.vue';

const form = useFormContext();
const { isCaption } = useChallengeCreationFlow();
const route = useRoute();

const stepper = inject('stepper');
const builder = inject('builder');

const hasImageUploaded = ref(false);

const isImplicit = computed(() => route.name === 'challenges.show');

const category = computed(() => {
    const categories = builder.categoryTree;
    return categories.length > 0 ? categories[categories.length - 1].name : 'Blank';
});

const tags = computed(() => {
    const tree = builder.categoryTree;
    const main = tree.length > 0 ? tree[tree.length - 1].name : null;
    return [main].concat(form.fields.challenge.tags);
});

function jumpToArticle() {
    stepper.scrollToSteps();

    setTimeout(() => {
        stepper.animated = true;
        stepper.reorder({
            slug: 'article-source',
            order: 4,
            select: true,
        });
    }, 300);
}

function updateArticleImage({ url }) {
    if (form.fields.challenge.article && !form.fields.challenge.article.image_url) {
        form.fields.challenge.article.image_url = url;
    }
}
</script>

<template>
    <div v-if="form.fields.challenge">
        <div v-if="! isImplicit" class="challenge-section-heading">
            <h2 class="challenge-section-heading__title">
                <b>Add an Image to your Question</b>
            </h2>
        </div>
        <div class="form-section-wrapper">
            <div class="flex flex-col gap-6">
                <div>
                    <label class="form-group__label">
                        <b>Upload your own image, graphic or illustration</b>
                        <i v-if="!isCaption" class="font-weight-normal ml-1">(optional, or you can fetch a free Unsplash or Pixabay image)</i>
                    </label>
                    <p class="form-block-help-text mb-0">
                        <i>Upload a JPEG or PNG up to 20mb in size. We'll resize it to 1200px width and proportional height to be mobile-friendly.</i>
                    </p>
                    <NFile
                        v-model="form.fields.challenge.primary_image"
                        :accept="['image/jpeg', 'image/jpg', 'image/png', 'image/gif']"
                        name="primary_image"
                        @loaded="() => hasImageUploaded = true"
                        @change="updateArticleImage"
                    />
                </div>
                <div v-if="! builder.isCaption && !hasImageUploaded">
                    <label class="form-group__label">
                        Or find a free one from Unsplash & Pixabay <i class="font-weight-normal text-dust-600">(optional)</i>
                    </label>
                    <p class="form-block-help-text">
                        We'll use your main category of <b>{{ category }}</b> and the keywords to fetch some relevant images. If you can't find one you like after a few tries, consider adding more secondary keywords to get better matches.
                    </p>
                    <RandomImages
                        v-model="form.fields.challenge.primary_image"
                        :tags="tags"
                        :prefetch="true"
                        @prefetch="form.fields.challenge.primary_image = ''"
                        @change="updateArticleImage"
                    >
                        <template #tags="{ fetch }">
                            <NTags v-model="form.fields.challenge.tags" @change="() => fetch()" />
                        </template>
                    </RandomImages>
                </div>
                <div v-if="! builder.isCaption && stepper.has('article-source')">
                    <label class="form-group__label">
                        Are you linking to an external <span class="underline">news article</span> or <span class="underline">blog post</span>? <i class="font-weight-normal text-dust-600">(optional)</i>
                    </label>
                    <p class="form-block-help-text">
                        If your news source, article, or blog post has its own image, you can use that main image for your challenge. We'll pull in the image using OpenGraph.
                    </p>
                    <div>
                        <button aria-label="button" type="button" class="flex items-center p-2 px-6 rounded-md shadow hover:bg-dust-600 hover:text-white border bg-dust-500 text-white transition ease-in-out duration-300" @click="jumpToArticle">
                            Jump to the Article Module
                            <NIcon as="image" class="ml-2"/>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
